import React from "react";
import Loader from "@components/Loader";
import ProductsFilter from "@components/catalog/ProductsFilter";
import ProductsTable from "@components/catalog/ProductsTable";
import { useProductsQuery, useDeleteProductsMutation } from "@queries/catalog";
import { useNavigate } from "react-router-dom";
import "@styles/opencart-admin.css";

export default function ProductsPage() {
  const [filters, setFilters] = React.useState({});
  const [page, setPage] = React.useState(1);
  const pageSize = 20;
  const navigate = useNavigate();

  const { data, isLoading, isFetching, isError, error } =
    useProductsQuery(filters, page, pageSize);

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const [selected, setSelected] = React.useState(new Set());
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const deleteMut = useDeleteProductsMutation();

  function handleToggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleToggleAll(ids) {
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  }

  function handleDeleteConfirmed() {
    const ids = [...selected];
    deleteMut.mutate(ids, {
      onSuccess: () => {
        setSelected(new Set());
        setConfirmOpen(false);
      },
    });
  }

  // Clear selection when page/filters change
  React.useEffect(() => { setSelected(new Set()); }, [page, filters]);

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      {confirmOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
        }}>
          <div style={{
            background: "#fff", borderRadius: 10, padding: 32, minWidth: 320,
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)", textAlign: "center",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Delete Products</div>
            <div style={{ marginBottom: 24, color: "#555" }}>
              Are you sure you want to permanently delete{" "}
              <strong>{selected.size}</strong> product{selected.size !== 1 ? "s" : ""}?
              <br />
              <span style={{ color: "#c00", fontSize: 13 }}>This cannot be undone.</span>
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                className="oc-btn ghost"
                onClick={() => setConfirmOpen(false)}
                disabled={deleteMut.isPending}
              >
                Cancel
              </button>
              <button
                className="oc-btn"
                style={{ background: "#dc2626", color: "#fff", borderColor: "#dc2626" }}
                onClick={handleDeleteConfirmed}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="oc-header">
        <h1>Products</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Products</span>
        </nav>
      </div>

      <div className="oc-card">
        <div style={{ marginBottom: 12 }}>
          <div className="title" style={{ marginBottom: 8 }}>Filter</div>
          <ProductsFilter
            value={filters}
            onChange={(f) => { setPage(1); setFilters(f); }}
          />
        </div>

        <div
          className="oc-toolbar"
          style={{
            marginBottom: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <strong>Product List</strong>
            {selected.size > 0 && (
              <span style={{ color: "#555", fontSize: 13 }}>
                {selected.size} selected
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {selected.size > 0 && (
              <button
                type="button"
                className="oc-btn"
                style={{ background: "#dc2626", color: "#fff", borderColor: "#dc2626" }}
                onClick={() => setConfirmOpen(true)}
              >
                🗑 Delete Selected ({selected.size})
              </button>
            )}
            <button
              type="button"
              className="oc-btn"
              onClick={() => navigate("/catalog/products/new")}
            >
              + Create Product
            </button>
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00" }}>Error: {error.message}</div>
        )}

        <ProductsTable
          rows={rows}
          selected={selected}
          onToggle={handleToggle}
          onToggleAll={handleToggleAll}
        />
      </div>

      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 12,
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong>{" "}
          ({total} total)
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="oc-btn ghost" disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
          <button className="oc-btn ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          <button className="oc-btn ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          <button className="oc-btn ghost" disabled={page >= pages} onClick={() => setPage(pages)}>Last »</button>
        </div>
      </div>
    </div>
  );
}
